import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';

import { ensureAnonymousUser } from '../auth/firebase';
import {
  useFingeringUpload,
  validateFile,
  isImageFile,
  MAX_IMAGE_BYTES_PER_PAGE,
  imagePageLimitMessage,
} from '../hooks/useFingeringUpload';
import { useGuestUploadCap } from '../hooks/useGuestUploadCap';
import { LoadingSpinner } from './LoadingSpinner';
import { setDraftFromFiles } from '../stores/scanDraftStore';
import {
  rememberUploadReturnTo,
  uploadNavigateState,
} from '../samples/sampleReturnTo';
import {
  captureFunnelEvent,
  reportGoogleAdsUploadStarted,
  trackButtonClick,
  type UploadKind,
} from '../services/telemetry';

const ACCEPT_ATTR =
  '.xml,.XML,.musicxml,.MUSICXML,.mxl,.MXL,.pdf,.PDF,' +
  '.jpg,.JPG,.jpeg,.JPEG,.png,.PNG,.webp,.WEBP,' +
  'image/jpeg,image/png,image/webp,application/octet-stream';

interface ScoreUploadBoxProps {
  /**
   * `guest` (home page): silently sign the visitor in as an anonymous Firebase
   * user, enforce the localStorage soft cap, and route to `/sign-up` once it's
   * hit. `member` (workspace): a normal upload for a signed-in account — no
   * soft cap, respects the project cap via `disabled`.
   */
  variant?: 'guest' | 'member';
  /** Telemetry source label. */
  source?: string;
  /** Member-only: block uploads (e.g. Free Plan project cap reached). */
  disabled?: boolean;
  /** Message shown when `disabled` is true. */
  disabledMessage?: string;
  /**
   * Max number of page images accepted in a single image (OMR) upload.
   * Guests and free-tier users get `1`; subscribed / credit-holding users get
   * up to `MAX_IMAGE_PAGES` (15). Single-document (PDF/XML/MXL) uploads are
   * unaffected. Defaults to `1`.
   */
  imagePageLimit?: number;
  /**
   * Visual layout. Prefer an explicit prop over viewport CSS when the parent
   * already mounts separate mobile/desktop instances (Home):
   * - `compact`: no dashed box / drag hint; dark upload icon inside the button
   * - `boxed`: dashed drop zone, yellow icon + drag hint above the button
   *   (icon is NOT inside the button)
   * - `camera`: rear-camera primary, file picker secondary.
   */
  layout?: 'compact' | 'boxed' | 'camera';
}

export type ScoreUploadBoxHandle = {
  /** Opens the rear-camera capture input (`capture="environment"`). */
  openCamera: () => void;
};

type UploadMethod = 'drop' | 'picker' | 'camera';

/**
 * Drag-and-drop + "select file" upload widget shared by the home page (guest
 * flow) and the workspace (member flow). Both reuse the standard fingering
 * upload pipeline (`uploadFileDirectly`), which creates a project and kicks off
 * generation; on success it navigates to the result page.
 */
export const ScoreUploadBox = forwardRef<
  ScoreUploadBoxHandle,
  ScoreUploadBoxProps
>(function ScoreUploadBox(
  {
    variant = 'guest',
    source = 'ScoreUploadBox',
    disabled = false,
    disabledMessage,
    imagePageLimit = 1,
    layout = 'boxed',
  },
  ref
) {
  const isCompact = layout === 'compact';
  const isCamera = layout === 'camera';
  const isGuest = variant === 'guest';
  const navigate = useNavigate();
  // Pass `true` so the upload hook's `createProject` runs. For guests the
  // anonymous user has a valid token even though the app treats them as "not
  // authenticated"; for members it's a normal authenticated create.
  const { uploadFileDirectly, uploadImagesDirectly, uploadStatus } =
    useFingeringUpload(true);
  const allowsMultipleImages = imagePageLimit > 1;
  // Soft cap only applies to guests; the hook is cheap and safe to always call.
  const { limitReached, recordUpload } = useGuestUploadCap();

  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [pickedName, setPickedName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);

  const goToSignUp = useCallback(
    (reason: string) => {
      captureFunnelEvent('signup_button_clicked', {
        source,
        cta: reason,
      });
      navigate('/sign-up');
    },
    [navigate, source]
  );

  // Shared success/failure tail for both upload paths. `result.isPdf` selects
  // the OMR/PDF-style result page vs the MusicXML editor page.
  const finishUpload = useCallback(
    (
      result: {
        success?: boolean;
        projectId?: string | null;
        isPdf?: boolean;
        needsHandSelection?: boolean;
        layout?: string | null;
        error?: string;
      },
      kind: UploadKind,
      initialBytes: number
    ) => {
      if (result?.success && result.projectId) {
        if (isGuest) {
          recordUpload();
        }
        // Single funnel event for both flows so guest vs member uploads are
        // one segmentable event (mirrors the backend `upload` event's
        // `is_guest`).
        captureFunnelEvent('upload_succeeded', {
          source,
          isPdf: result.isPdf,
          is_guest: isGuest,
          kind,
          initialBytes,
          needsHandSelection: Boolean(result.needsHandSelection),
          layout: result.layout ?? null,
        });
        rememberUploadReturnTo(source);
        const navState = uploadNavigateState(source);
        navigate(
          result.isPdf
            ? `/pdf-project/${result.projectId}`
            : `/project/${result.projectId}`,
          navState ? { state: navState } : undefined
        );
        return;
      }
      setValidationError(result.error || 'Upload failed. Please try again.');
      captureFunnelEvent('upload_fail', {
        source,
        is_guest: isGuest,
        kind,
        initialBytes,
        reason: 'upload_unsuccessful',
      });
    },
    [isGuest, recordUpload, source, navigate]
  );

  const handleUploadError = useCallback(
    (
      err: unknown,
      failureContext?: {
        kind: UploadKind;
        initialBytes: number;
      }
    ) => {
      console.error('Score upload failed:', err);
      // For guests, `ensureAnonymousUser` throws if anonymous sign-in is
      // disabled in Firebase (`auth/admin-restricted-operation`). Surface a
      // friendly message instead of failing silently.
      const code = (err as { code?: string })?.code;
      setValidationError(
        code === 'auth/admin-restricted-operation'
          ? 'Guest uploads are temporarily unavailable. Please sign up to generate fingerings.'
          : 'Something went wrong starting your upload. Please try again.'
      );
      // Only when `upload_started` already fired (failureContext present).
      if (failureContext) {
        const message = (err as { message?: string })?.message;
        captureFunnelEvent('upload_fail', {
          source,
          is_guest: isGuest,
          ...failureContext,
          reason: code || message || 'exception',
        });
      }
    },
    [source, isGuest]
  );

  const handleFiles = useCallback(
    async (files: File[], uploadMethod: UploadMethod) => {
      if (disabled || files.length === 0) return;
      setValidationError(null);
      setPickedName(files[0]?.name ?? null);

      // Guest soft cap: once free tries are used, nudge to sign-up instead of
      // uploading again. (UX only — not a security control.)
      if (isGuest && limitReached) {
        trackButtonClick(source, 'Soft Cap Reached');
        goToSignUp('Guest upload limit reached');
        return;
      }

      const trackAdsConversion = (extra: {
        kind: UploadKind;
        initialBytes: number;
        pageCount?: number;
      }) => {
        reportGoogleAdsUploadStarted({
          source,
          is_guest: isGuest,
          upload_method: uploadMethod,
          ...extra,
        });
      };

      const allImages = files.every(isImageFile);

      // Mixed selections, or multiple non-image files, aren't a valid upload:
      // a document is always single-file, images go as a same-type bundle.
      if (!allImages && files.length > 1) {
        setValidationError(
          'Please upload a single score file (PDF/XML/MXL), or multiple page images (JPG/PNG/WEBP).'
        );
        return;
      }

      // --- Image (OMR) path --------------------------------------------
      if (allImages) {
        if (files.length > imagePageLimit) {
          setValidationError(imagePageLimitMessage(imagePageLimit));
          return;
        }
        const initialBytes = files.reduce((sum, f) => sum + f.size, 0);
        const oversized = files.find((f) => f.size > MAX_IMAGE_BYTES_PER_PAGE);
        if (oversized) {
          setValidationError(
            `Image "${oversized.name}" exceeds the 10MB per-page limit. Please choose a smaller image.`
          );
          captureFunnelEvent('upload_fail', {
            source,
            is_guest: isGuest,
            kind: 'image',
            initialBytes,
            reason: 'file_oversize',
          });
          return;
        }

        // Members who can upload multi-page bundles go to the reorder/staging
        // page first (add/remove/reorder before generating). Nothing is
        // uploaded until they hit "Generate fingering" there. Guests / free
        // (single-page) users upload their one image immediately.
        if (allowsMultipleImages) {
          setIsUploading(true);
          try {
            trackButtonClick(source, 'Score Upload', {
              fileCount: files.length,
              kind: 'image',
              initialBytes,
              staged: true,
            });
            await setDraftFromFiles(files);
            trackAdsConversion({
              kind: 'image',
              initialBytes,
              pageCount: files.length,
            });
            navigate('/scan/review');
          } catch (err) {
            handleUploadError(err);
          } finally {
            setIsUploading(false);
          }
          return;
        }

        setIsUploading(true);
        try {
          trackButtonClick(source, 'Score Upload', {
            fileCount: files.length,
            kind: 'image',
          });
          captureFunnelEvent('upload_started', {
            source,
            is_guest: isGuest,
            kind: 'image',
            initialBytes,
            pageCount: files.length,
            upload_method: uploadMethod,
          });
          trackAdsConversion({
            kind: 'image',
            initialBytes,
            pageCount: files.length,
          });
          if (isGuest) {
            await ensureAnonymousUser();
          }
          const result = await uploadImagesDirectly(files);
          finishUpload(result, 'image', initialBytes);
        } catch (err) {
          handleUploadError(err, {
            kind: 'image',
            initialBytes,
          });
        } finally {
          setIsUploading(false);
        }
        return;
      }

      // --- Single-document path (PDF / XML / MXL / MusicXML) ------------
      const file = files[0];
      const { isValidExtension, isValidSize } = validateFile(file);
      if (!isValidExtension) {
        setValidationError(
          'Invalid file format. Please select a .xml, .musicxml, .mxl, .pdf, or an image (.jpg, .png, .webp) file.'
        );
        return;
      }
      // `xml`/`musicxml`/`mxl` all map to the MusicXML editor path; only `.pdf`
      // takes the PDF/OMR result page.
      const kind: UploadKind = file.name.toLowerCase().endsWith('.pdf')
        ? 'pdf'
        : 'xml';
      const initialBytes = file.size;
      if (!isValidSize) {
        setValidationError(
          'File size exceeds the 10MB limit. Please select a smaller file.'
        );
        captureFunnelEvent('upload_fail', {
          source,
          is_guest: isGuest,
          kind,
          initialBytes,
          reason: 'file_oversize',
        });
        return;
      }

      setIsUploading(true);
      try {
        trackButtonClick(source, 'Score Upload', { fileName: file.name });
        captureFunnelEvent('upload_started', {
          source,
          is_guest: isGuest,
          kind,
          initialBytes,
          upload_method: uploadMethod,
        });
        trackAdsConversion({ kind, initialBytes });
        // Guests need an anonymous Firebase user so the upload carries a
        // verifiable token. Signed-in members already have one.
        if (isGuest) {
          await ensureAnonymousUser();
        }

        const result = await uploadFileDirectly(file);
        finishUpload(result, kind, initialBytes);
      } catch (err) {
        handleUploadError(err, { kind, initialBytes });
      } finally {
        setIsUploading(false);
      }
    },
    [
      disabled,
      isGuest,
      limitReached,
      goToSignUp,
      source,
      imagePageLimit,
      allowsMultipleImages,
      navigate,
      uploadFileDirectly,
      uploadImagesDirectly,
      finishUpload,
      handleUploadError,
    ]
  );

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    uploadMethod: UploadMethod
  ) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) {
      void handleFiles(files, uploadMethod);
    }
    // Reset so selecting the same file again re-triggers change.
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (isUploading || disabled) return;
    const files = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
    if (files.length > 0) {
      void handleFiles(files, 'drop');
    }
  };

  const openFilePicker = () => {
    if (isUploading || disabled) return;
    fileInputRef.current?.click();
  };

  const openCamera = useCallback(() => {
    if (isUploading || disabled) return;
    cameraInputRef.current?.click();
  }, [isUploading, disabled]);

  useImperativeHandle(ref, () => ({ openCamera }), [openCamera]);

  const isInteractive = !isUploading && !disabled;

  const statusBlock = (
    <>
      {disabled && disabledMessage && (
        <p className="mt-3 text-sm text-orange-500">{disabledMessage}</p>
      )}
      {isUploading && uploadStatus && (
        <div
          className={`
            mt-3 flex items-center justify-center gap-2 text-sm text-orange-500
          `}
        >
          <LoadingSpinner variant="small" />
          {uploadStatus}
        </div>
      )}
      {!isUploading && validationError && (
        <p className="mt-3 text-sm text-red-600">{validationError}</p>
      )}
    </>
  );

  // Friction-removers shown to guests. The home hero already names the
  // accepted formats, so guests get this line instead of "Accepted: …".
  const trustLine = (
    <p
      className={`
        mt-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1
        text-center text-[13px] text-gray-500
      `}
    >
      <span>Free preview</span>
      <span aria-hidden="true">·</span>
      <span>~15 seconds</span>
    </p>
  );

  const legalNotice = (
    <p
      className={`
        mx-auto mt-2 max-w-sm text-center text-[11px] leading-snug text-gray-400
        lg:mt-4
      `}
    >
      By uploading, you agree to our{' '}
      <Link to="/terms-of-service" className="underline hover:text-gray-600">
        Terms of Service
      </Link>{' '}
      and{' '}
      <Link to="/privacy-policy" className="underline hover:text-gray-600">
        Privacy Policy
      </Link>
      .
    </p>
  );

  if (isCamera) {
    return (
      <div className="mx-auto w-full">
        <div className="rounded-[20px] bg-[#FCF7E4] px-[18px] pt-5 pb-[18px]">
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handleInputChange(e, 'camera')}
            className="sr-only"
            aria-label="Take a photo of your sheet music"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            onChange={(e) => handleInputChange(e, 'picker')}
            className="sr-only"
            aria-label="Upload a PDF, MusicXML, or image of your sheet music"
          />

          <button
            type="button"
            onClick={() => {
              void trackButtonClick(source, 'Take a Photo');
              openCamera();
            }}
            disabled={!isInteractive}
            className={`
              flex w-full items-center justify-center gap-3 rounded-full
              bg-yellow-400 px-5 py-[18px] text-[19px] font-bold text-gray-800
              shadow-[0_4px_0_#eab308] transition-[transform,background]
              duration-100
              hover:bg-yellow-500
              active:scale-[0.985]
              ${
                !isInteractive
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer'
              }
            `}
          >
            <FontAwesomeIcon
              icon={faCamera}
              aria-hidden="true"
              className="text-xl"
            />
            {isUploading ? 'Uploading…' : 'Take a photo'}
          </button>

          <p className="mt-2 text-center text-[11px] leading-snug text-gray-500">
            Photograph a full page for best results.
          </p>

          <div
            className={`
              mx-1 my-[13px] flex items-center gap-3 text-[13px] text-gray-500
              before:h-px before:flex-1 before:bg-gray-200 before:content-['']
              after:h-px after:flex-1 after:bg-gray-200 after:content-['']
            `}
          >
            or
          </div>

          <button
            type="button"
            onClick={() => {
              void trackButtonClick(source, 'Upload an image, PDF or MusicXML');
              openFilePicker();
            }}
            disabled={!isInteractive}
            className={`
              flex w-full items-center justify-center gap-2.5 rounded-full
              border-[1.5px] border-gray-200 bg-transparent px-5 py-3 text-sm
              font-semibold text-gray-800 transition-colors duration-150
              hover:border-gray-500
              active:scale-[0.985]
              ${
                !isInteractive
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer'
              }
            `}
          >
            <FontAwesomeIcon
              icon={faCloudArrowUp}
              aria-hidden="true"
              className="text-base"
            />
            Upload an image, PDF or MusicXML
          </button>

          {pickedName && (
            <div
              className={`
                mt-3.5 flex items-center gap-2 rounded-xl border bg-white px-3.5
                py-2.5 text-left text-sm
                ${validationError ? 'border-red-300' : 'border-gray-200'}
              `}
            >
              <span
                className={`
                  h-2 w-2 shrink-0 rounded-full
                  ${
                    isUploading
                      ? 'bg-yellow-400'
                      : validationError
                        ? 'bg-red-600'
                        : 'bg-emerald-600'
                  }
                `}
                aria-hidden="true"
              />
              <span className="truncate font-semibold text-gray-800">
                {pickedName}
                {isUploading
                  ? ' — uploading…'
                  : validationError
                    ? ' — failed'
                    : ' — ready'}
              </span>
            </div>
          )}

          {trustLine}

          {statusBlock}
        </div>
        {legalNotice}
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-xl">
      <div
        className={
          isCompact
            ? 'flex h-full flex-col items-center justify-center text-center'
            : `
              flex h-full flex-col items-center justify-center rounded-2xl
              border-2 border-dashed px-4 pt-6 pb-10 text-center transition-all
              duration-150
              lg:px-6 lg:pt-10 lg:pb-8
              ${
                disabled
                  ? 'border-gray-200 bg-gray-50'
                  : isDragging
                    ? `
                      border-yellow-500 bg-yellow-50 shadow-2xl ring-4
                      ring-yellow-300
                    `
                    : 'border-yellow-400 bg-white shadow-xl'
              }
            `
        }
        onDragOver={(e) => {
          e.preventDefault();
          if (isInteractive) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        {/* Boxed layout: yellow icon + drag hint above the button.
            Compact layout: no chrome — icon goes inside the button only. */}
        {!isCompact && (
          <>
            <FontAwesomeIcon
              icon={faCloudArrowUp}
              aria-hidden="true"
              className="mb-2 text-4xl text-yellow-500 lg:mb-3"
            />
            <p className="mb-3 text-sm text-gray-500 lg:mb-5">
              Drag &amp; drop your piano score
              {allowsMultipleImages ? ' or photos' : ''}, or
            </p>
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_ATTR}
          multiple={allowsMultipleImages}
          onChange={(e) => handleInputChange(e, 'picker')}
          className="hidden"
          aria-label="Select a sheet music file or page images to upload"
        />

        <button
          type="button"
          onClick={openFilePicker}
          disabled={!isInteractive}
          className={`
            inline-flex items-center justify-center gap-2 rounded-full
            bg-yellow-400 font-bold text-gray-800
            transition-[transform,background] duration-100
            hover:bg-yellow-500
            active:scale-[0.985]
            ${
              isCompact
                ? 'px-6 py-3 font-semibold'
                : `px-6 py-[18px] text-[19px] shadow-[0_4px_0_#eab308] lg:px-8`
            }
            ${
              !isInteractive
                ? 'cursor-not-allowed opacity-50'
                : `cursor-pointer`
            }
          `}
        >
          {isCompact && (
            <FontAwesomeIcon
              icon={faCloudArrowUp}
              aria-hidden="true"
              className="text-gray-800"
            />
          )}
          {isUploading ? 'Uploading…' : 'Select Score to Upload'}
        </button>

        {isGuest && isCompact && (
          <p className="mt-2 text-xs text-gray-400">free preview generation</p>
        )}
        {isGuest && !isCompact ? (
          trustLine
        ) : (
          <p
            className={
              isCompact
                ? isGuest
                  ? 'mt-1 text-xs text-gray-400'
                  : 'mt-5 text-xs text-gray-400'
                : 'mt-5 text-xs text-gray-400 lg:mt-6'
            }
          >
            Accepted: musicxml, pdf, jpg, png
            {isCompact && (
              <>
                <br />
                max 10MB per file
              </>
            )}
          </p>
        )}

        {statusBlock}
      </div>

      {/* Content-rights + ToS/Privacy notice, shown for both variants: guests
          have no signup step, and the signup form no longer has a ToS
          checkbox, so this is the members' consent reminder at upload too. */}
      {legalNotice}
    </div>
  );
});
